/**
 * User Preferences Service
 * Handles user-level settings and feature flags
 * Path: /users/{uid}/private/preferences
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { logger } from '@/lib/logger';
import { db } from '@/lib/firebase';

/**
 * User feature flags
 */
export interface UserFeatureFlags {
  notepad?: boolean;
  // Add more user-level feature flags here
}

/**
 * User preferences document
 */
export interface UserPreferences {
  flags: UserFeatureFlags;
  activeTeamId?: string | null;
  updatedAt?: Date;
}

/**
 * Default user preferences
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  flags: {
    notepad: true, // Enable notepad by default
  },
  activeTeamId: null,
};

/**
 * Convert Firestore document to UserPreferences
 */
function docToPreferences(docSnap: DocumentSnapshot<DocumentData>): UserPreferences {
  const data = docSnap.data();
  if (!data) {
    return DEFAULT_PREFERENCES;
  }

  return {
    flags: {
      notepad: data.flags?.notepad ?? DEFAULT_PREFERENCES.flags.notepad,
    },
    activeTeamId: data.activeTeamId ?? null,
    updatedAt: data.updatedAt?.toDate(),
  };
}

/**
 * Get user preferences
 */
export async function getUserPreferences(userId: string): Promise<UserPreferences> {
  try {
    const prefsRef = doc(db, 'users', userId, 'private', 'preferences');
    const prefsSnap = await getDoc(prefsRef);

    if (!prefsSnap.exists()) {
      // Initialize with defaults
      await setDoc(prefsRef, {
        ...DEFAULT_PREFERENCES,
        updatedAt: serverTimestamp(),
      });
      return DEFAULT_PREFERENCES;
    }

    return docToPreferences(prefsSnap);
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to get preferences:', error);
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Update user preferences
 */
export async function updateUserPreferences(
  userId: string,
  preferences: Partial<UserPreferences>
): Promise<UserPreferences> {
  try {
    const prefsRef = doc(db, 'users', userId, 'private', 'preferences');
    const prefsSnap = await getDoc(prefsRef);

    const updates: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (preferences.flags) {
      updates.flags = preferences.flags;
    }

    if ('activeTeamId' in preferences) {
      updates.activeTeamId = preferences.activeTeamId ?? null;
    }

    if (prefsSnap.exists()) {
      await updateDoc(prefsRef, updates);
    } else {
      await setDoc(prefsRef, {
        ...DEFAULT_PREFERENCES,
        ...updates,
      });
    }

    return getUserPreferences(userId);
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to update preferences:', error);
    throw error;
  }
}

/**
 * Get a specific feature flag value
 */
export async function getUserFlag(userId: string, flag: keyof UserFeatureFlags): Promise<boolean> {
  try {
    const preferences = await getUserPreferences(userId);
    return preferences.flags[flag] ?? false;
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to get flag:', error);
    return false;
  }
}

/**
 * Get the user's active team preference
 * Returns null if not set.
 * INV-SEC-UI-CTX-1: This is UX-only, not a security anchor.
 */
export async function getActiveTeamPreference(userId: string): Promise<string | null> {
  try {
    const prefs = await getUserPreferences(userId);
    return prefs.activeTeamId ?? null;
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to get active team preference:', error);
    return null;
  }
}

/**
 * Set the user's active team preference
 * Uses merge semantics to avoid overwriting other preference fields.
 * INV-SEC-UI-CTX-1: This is UX-only, not a security anchor.
 */
export async function setActiveTeamPreference(
  userId: string,
  teamId: string | null
): Promise<void> {
  try {
    const prefsRef = doc(db, 'users', userId, 'private', 'preferences');
    await setDoc(
      prefsRef,
      {
        activeTeamId: teamId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    logger.info('[UserPreferencesService] Active team preference updated:', { teamId });
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to set active team preference:', error);
    throw error;
  }
}

/**
 * Update a specific feature flag
 */
export async function updateUserFlag(
  userId: string,
  flag: keyof UserFeatureFlags,
  value: boolean
): Promise<void> {
  try {
    const preferences = await getUserPreferences(userId);
    await updateUserPreferences(userId, {
      flags: {
        ...preferences.flags,
        [flag]: value,
      },
    });
  } catch (error) {
    logger.error('[UserPreferencesService] Failed to update flag:', error);
    throw error;
  }
}
